<script setup>
// Aceleres: fluxo único de "iniciar nova conversa" a partir de um telefone.
//
// Nasceu inline no ChatList.vue (Chat de Atendimento) e foi extraído para que a
// página de Contatos use exatamente o mesmo modal de seleção de instância, as
// mesmas regras de disponibilidade, o mesmo aviso de contato já em atendimento
// por outro vendedor e a mesma criação de conversa. Quem hospeda o componente
// só informa o telefone; todo o resto acontece aqui.
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import {
  buildQuickStartChannels,
  eligibleInboxesForPhone,
  isEligibleQuickStartInbox,
  isOwnedPersonalQuickStartInbox,
} from 'dashboard/helper/aceleresQuickStartChannels';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});

const emit = defineEmits(['started', 'close']);

const router = useRouter();

const currentUser = useMapGetter('getCurrentUser');
const inboxesList = useMapGetter('inboxes/getInboxes');

const currentUserId = computed(() => Number(currentUser.value?.id || 0));

const phone = ref('');
const selectedContactId = ref(null);
const quickInboxes = ref([]);
const quickEligibleInboxIds = ref([]);
const quickInboxPickerOpen = ref(false);
const quickCreating = ref(false);
const ownershipCheck = ref(null);
const ownershipMessageIntent = ref('');
const ownershipChecking = ref(false);
const ownershipRequesting = ref(false);
const ownershipRequestSent = ref(false);
const ownershipApproverName = ref('');
const ownershipRequestedInboxId = ref(null);

const quickStartChannels = computed(() =>
  buildQuickStartChannels({
    inboxes: inboxesList.value,
    extraInboxes: quickInboxes.value,
    eligibleInboxIds: quickEligibleInboxIds.value,
    currentUserId: currentUserId.value,
  })
);

const isBusy = computed(() => quickCreating.value || ownershipChecking.value);

// A inbox escolhida é a instância pessoal DESTE usuário? É o que decide se a
// conversa nasce Pessoal ou como atendimento comercial. A mesma pergunta é
// respondida no servidor (Aceleres::PersonalInstanceStart#own_personal_inbox?)
// — aqui ela existe só para montar o payload e o aviso da tela.
function isOwnPersonalInbox(inboxId) {
  const inbox =
    (inboxesList.value || []).find(
      item => Number(item.id) === Number(inboxId)
    ) ||
    (quickInboxes.value || []).find(
      item => Number(item.id) === Number(inboxId)
    );

  return isOwnedPersonalQuickStartInbox(inbox, currentUserId.value);
}

// Carteirizacao e atendimento sao coisas diferentes (o dono do lead nem sempre
// e quem esta com a conversa aberta), entao o aviso nao pode dizer "sendo
// atendido" quando o que existe e caracterizacao.
const ownershipVerb = computed(() =>
  ownershipCheck.value?.owner_source === 'carteira'
    ? 'já está caracterizado para'
    : 'já está sendo atendido por'
);

// Lead da empresa SEM responsável atual, barrado na conexão pessoal. Não há
// dono a citar -- quem detém o lead é a empresa, e quem decide o pedido é o
// gestor. O aviso precisa de texto próprio: "está sendo atendido por —" não
// diria nada.
const ownershipIsCompanyLead = computed(
  () => ownershipCheck.value?.owner_source === 'company_lead'
);

function reset() {
  phone.value = '';
  selectedContactId.value = null;
  quickInboxes.value = [];
  quickEligibleInboxIds.value = [];
  quickInboxPickerOpen.value = false;
  ownershipCheck.value = null;
  ownershipMessageIntent.value = '';
  ownershipApproverName.value = '';
  ownershipRequestedInboxId.value = null;
}

function closePicker() {
  quickInboxPickerOpen.value = false;
  emit('close');
}

// Ponto de entrada do fluxo: recebe o telefone e abre o seletor de instância.
async function startForPhone(rawPhone, selectedInboxId = null, contactId = null) {
  const value = (rawPhone || '').trim();
  if (!value) return;

  phone.value = value;
  selectedContactId.value = contactId;
  ownershipCheck.value = null;
  quickInboxPickerOpen.value = false;

  const eligibles = eligibleInboxesForPhone(
    inboxesList.value,
    currentUserId.value
  );
  quickInboxes.value = eligibles;
  quickEligibleInboxIds.value = eligibles
    .filter(inbox => isEligibleQuickStartInbox(inbox, currentUserId.value))
    .map(inbox => Number(inbox.id));

  const enabledChannels = quickStartChannels.value.filter(
    channel => channel.canStart
  );
  if (selectedInboxId) {
    const selected = enabledChannels.find(
      channel => channel.id === Number(selectedInboxId)
    );
    if (!selected) {
      alert('O canal selecionado está desconectado ou indisponível.');
      return;
    }
    await startPhoneConversation(selected.id);
    return;
  }
  if (enabledChannels.length === 0) {
    if (quickStartChannels.value.length > 0) {
      quickInboxPickerOpen.value = true;
      return;
    }
    alert('Nenhum canal de WhatsApp conectado para iniciar a conversa.');
    return;
  }
  quickInboxPickerOpen.value = true;
}

async function startPhoneConversation(inboxId) {
  if (!inboxId) return;
  ownershipRequestedInboxId.value = Number(inboxId);
  const accId = props.accountId;
  const personalStart = isOwnPersonalInbox(inboxId);
  const src = phone.value;
  ownershipChecking.value = true;
  try {
    const check = await window.axios.post(
      `/api/v1/accounts/${accId}/contact_ownership/check`,
      {
        phone: src,
        inbox_id: inboxId,
      }
    );
    ownershipCheck.value = check.data;
    if (check.data?.active_conversation) {
      if (check.data.requires_transfer_request) return;
      openExistingConversation();
      return;
    }
    if (check.data && check.data.belongs_to_other) return;

    const existingConversationInboxId = Number(
      check.data?.existing_conversation_inbox_id || 0
    );
    if (
      check.data &&
      check.data.existing_conversation_id &&
      existingConversationInboxId === Number(inboxId)
    ) {
      quickInboxPickerOpen.value = false;
      emit('started', { personal: personalStart });
      reset();
      router.push(
        `/app/accounts/${accId}/conversations/${check.data.existing_conversation_id}`
      );
      return;
    }
  } catch (e) {
    console.warn('ownership check failed', e);
    if (e?.response?.status === 422) {
      ownershipCheck.value = e.response.data || null;
      alert(
        e.response.data?.message ||
          'O canal selecionado está desconectado ou indisponível.'
      );
      return;
    }
    ownershipCheck.value = null;
    alert(
      'Não foi possível confirmar a conexão do canal. Tente novamente antes de iniciar a conversa.'
    );
    return;
  } finally {
    ownershipChecking.value = false;
  }

  quickCreating.value = true;
  try {
    const digits = (src || '').replace(/\D/g, '');
    const phoneE164 = digits.startsWith('55') ? `+${digits}` : `+55${digits}`;

    // Usa o contato identificado pelo numero; cria um novo apenas se nao existe.
    let contactId =
      selectedContactId.value || ownershipCheck.value?.contact_id || null;
    if (!contactId) {
      try {
        const r = await window.axios.post(`/api/v1/accounts/${accId}/contacts`, {
          name: phoneE164,
          phone_number: phoneE164,
        });
        contactId = r.data?.payload?.contact?.id || r.data?.id;
      } catch (e) {
        const dup =
          e?.response?.data?.contact_id ||
          e?.response?.data?.contact?.id ||
          e?.response?.data?.payload?.contact?.id;
        if (dup) {
          contactId = dup;
        } else {
          const search = await window.axios.get(
            `/api/v1/accounts/${accId}/contacts/search?q=${encodeURIComponent(phoneE164)}`
          );
          contactId = search.data?.payload?.[0]?.id;
        }
      }
    }
    if (!contactId) throw new Error('Nao conseguiu criar/encontrar contato.');

    const createConversationParams = {
      source_id: phoneE164.replace('+', ''),
      inbox_id: inboxId,
      contact_id: contactId,
      manual_start_new_conversation: true,
    };

    // Este fluxo só chega até aqui depois que a checagem de ownership (acima)
    // confirmou que o lead não pertence a outro vendedor.
    //
    // Instância da empresa: selecionar o lead + a instância e efetivamente
    // iniciar a conversa É o início do atendimento (regra 4) — não é um clique
    // acidental (regra 3), então a conversa já nasce atribuída ao vendedor e
    // marcada como iniciada, para cair direto em "Meus" em vez de "Novos"
    // (regra 1), mesmo que ele nunca chegue a mandar mensagem.
    //
    // Instância PESSOAL: é o oposto, e de propósito. O número é do vendedor, a
    // conversa é dele, e usá-la não pode virar atendimento da empresa. Ela
    // nasce na caixa Pessoais e fica lá.
    //
    // `attendance_started: false` é escrito explicitamente, não omitido:
    // `ConversationState.started?` só respeita o atributo quando a chave
    // EXISTE — sem ela, a primeira resposta preenche `first_reply_created_at`
    // e a conversa passaria a contar como iniciada, saindo de Pessoais para
    // Meus sozinha. O responsável é o próprio dono do número, o que a mantém
    // em Pessoais (a saída de lá exige atribuída a mim E iniciada) e evita a
    // conversa pessoal sem responsável, que a policy do detalhe bloqueia.
    //
    // Exceção (manual de regras, 5.1): lead da empresa liberado pelo gestor
    // para atendimento no número pessoal NÃO é atendimento pessoal. Nasce em
    // Meus e segue as regras corporativas desde o primeiro momento — em
    // Pessoais ele não geraria card, não aplicaria carterização e não
    // apareceria para o gestor, tornando invisível justamente o atendimento
    // que a autorização liberou.
    const personalBoxStart =
      personalStart && !ownershipCheck.value?.authorized_company_lead;

    if (currentUserId.value > 0) {
      createConversationParams.assignee_id = currentUserId.value;
      createConversationParams.additional_attributes = personalBoxStart
        ? {
            attendance_started: false,
            personal_instance_start: true,
          }
        : {
            attendance_started: true,
            rodizio_locked_reason: personalStart
              ? 'personal_instance_authorized'
              : 'manual_start',
          };
    }

    // 3) cria conversa SEM mensagem -> cai no compositor pronto pra digitar
    const conv = await window.axios.post(
      `/api/v1/accounts/${accId}/conversations`,
      createConversationParams
    );
    const convDisplayId = conv.data?.id || conv.data?.payload?.id;
    quickInboxPickerOpen.value = false;
    emit('started', { personal: personalBoxStart });
    reset();
    router.push({
      name: 'inbox_conversation',
      params: { accountId: accId, conversation_id: convDisplayId },
    });
  } catch (e) {
    if (e?.response?.status === 409 && e.response.data?.active_conversation) {
      ownershipCheck.value = e.response.data;
      return;
    }
    console.error(e);
    alert(
      'Erro: ' +
        (e?.response?.data?.message ||
          e?.message ||
          'falha ao iniciar conversa')
    );
  } finally {
    quickCreating.value = false;
  }
}

function openExistingConversation() {
  const conversationId = ownershipCheck.value?.existing_conversation_id;
  if (!conversationId) return;
  quickInboxPickerOpen.value = false;
  router.push(`/app/accounts/${props.accountId}/conversations/${conversationId}`);
  reset();
  emit('close');
}

async function requestOwnership(messageIntent) {
  if (!ownershipCheck.value || !ownershipCheck.value.belongs_to_other) return;
  ownershipRequesting.value = true;
  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${props.accountId}/contact_ownership/request`,
      {
        phone: phone.value,
        message_intent: messageIntent || null,
        owner_chatwoot_user_id: ownershipCheck.value.owner_chatwoot_user_id,
        inbox_id: ownershipCheck.value.inbox_id,
        requested_inbox_id: ownershipRequestedInboxId.value,
        inbox_name: ownershipCheck.value.inbox_name,
        conversation_id: ownershipCheck.value.existing_conversation_id,
        contact_id: ownershipCheck.value.contact_id,
      }
    );
    ownershipApproverName.value = response.data?.approver_name || '';
    ownershipRequestSent.value = true;
  } catch (e) {
    alert('Erro: ' + (e?.response?.data?.error || e?.message || 'falha'));
  } finally {
    ownershipRequesting.value = false;
  }
}

function closeOwnershipRequestSent() {
  ownershipRequestSent.value = false;
  reset();
  emit('close');
}

// Cancelar o aviso devolve o usuário ao seletor de canal, que segue montado.
function dismissOwnershipCheck() {
  ownershipCheck.value = null;
  if (!quickInboxPickerOpen.value) emit('close');
}

defineExpose({ startForPhone, reset, isBusy });
</script>

<template>
  <span class="contents">
    <!-- Modal seletor de canal/instância -->
    <Teleport to="body">
      <div
        v-if="quickInboxPickerOpen"
        class="acl-quick-start-overlay"
        @click.self="closePicker"
      >
        <div
          class="acl-quick-start-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="acl-quick-start-title"
        >
          <header class="acl-quick-start-modal__header">
            <div>
              <h3
                id="acl-quick-start-title"
                class="acl-quick-start-modal__title"
              >
                Selecione um canal
              </h3>
              <p class="acl-quick-start-modal__description">
                Iniciar nova conversa com
                <strong>{{ phone }}</strong>
              </p>
            </div>
            <button
              type="button"
              class="acl-quick-start-modal__close"
              @click="closePicker"
            >
              <span class="i-lucide-x size-5" aria-hidden="true" />
            </button>
          </header>
          <div class="acl-quick-start-modal__list">
            <button
              v-for="channel in quickStartChannels"
              :key="channel.id"
              type="button"
              class="acl-quick-start-modal__channel"
              :class="{
                'acl-quick-start-modal__channel--disabled': !channel.canStart,
              }"
              :disabled="
                quickCreating || ownershipChecking || !channel.canStart
              "
              @click="startPhoneConversation(channel.id)"
            >
              <span
                class="acl-quick-start-modal__icon"
                :style="{ color: channel.icon.color }"
                aria-hidden="true"
              >
                <span :class="channel.icon.icon" />
              </span>
              <span class="acl-quick-start-modal__content">
                <span class="acl-quick-start-modal__name">
                  {{ channel.name }}
                  <span
                    v-if="channel.isPersonal"
                    class="acl-quick-start-modal__badge"
                  >
                    Pessoal
                  </span>
                </span>
                <span class="acl-quick-start-modal__subtitle">
                  {{ channel.subtitle }}
                </span>
              </span>
              <span
                v-if="channel.canStart"
                class="acl-quick-start-modal__action"
              >
                Escolher instância
                <span class="i-lucide-chevron-right size-3.5" />
              </span>
              <span v-else class="acl-quick-start-modal__blocked">
                {{ channel.disabledReason }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="ownershipCheck?.active_conversation && ownershipCheck.requires_transfer_request"
        class="acl-quick-start-overlay acl-quick-start-overlay--stacked"
        @click.self="dismissOwnershipCheck"
      >
        <div class="bg-n-solid-1 border border-n-strong rounded-xl w-full max-w-md p-6">
          <h3 class="text-base font-semibold text-n-slate-12 mb-3">Atendimento já aberto</h3>
          <p class="text-sm text-n-slate-11 mb-4">
            Este contato já tem um atendimento aberto na instância
            <strong>{{ ownershipCheck.inbox_name }}</strong>
            <span v-if="ownershipCheck.owner_name"> com {{ ownershipCheck.owner_name }}</span>.
            Peça ao responsável ou ao gestor a transferência pelo atendimento existente.
          </p>
          <div class="flex gap-2 justify-end">
            <button class="px-4 py-2 text-n-slate-11 text-sm" @click="dismissOwnershipCheck">Cancelar</button>
            <button
              v-if="ownershipCheck.can_open_existing_conversation"
              class="px-5 py-2 bg-n-brand text-white rounded-lg text-sm font-semibold"
              @click="openExistingConversation"
            >Abrir atendimento existente</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal de aviso: contato já em atendimento por outro vendedor -->
    <Teleport to="body">
      <div
        v-if="ownershipCheck && ownershipCheck.belongs_to_other"
        class="acl-quick-start-overlay acl-quick-start-overlay--stacked"
        @click.self="
          ownershipRequestSent
            ? closeOwnershipRequestSent()
            : dismissOwnershipCheck()
        "
      >
        <div
          class="bg-n-solid-1 border border-n-strong rounded-xl w-full max-w-md p-6"
        >
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            mode="out-in"
          >
            <div
              v-if="ownershipRequestSent"
              key="sent"
              class="py-2 text-center"
            >
              <div
                class="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-n-teal-3"
              >
                <span class="i-lucide-check size-7 text-n-teal-11" />
              </div>
              <h3 class="text-lg font-semibold text-n-slate-12">
                Solicitação enviada
              </h3>
              <p class="mx-auto mt-2 max-w-xs text-sm text-n-slate-11">
                <strong class="font-medium text-n-slate-12">{{
                  ownershipApproverName || ownershipCheck.owner_name
                }}</strong>
                vai receber seu pedido de autorização. Assim que for aprovado,
                você poderá iniciar a conversa.
              </p>
              <button
                class="mt-6 w-full rounded-lg bg-n-brand py-2.5 text-sm font-semibold text-white hover:opacity-90"
                @click="closeOwnershipRequestSent"
              >
                Entendi
              </button>
            </div>
            <div v-else key="form">
              <div class="flex items-center gap-2 mb-3">
                <span class="i-lucide-shield-alert size-6 text-amber-500" />
                <h3 class="text-base font-semibold text-n-slate-12">
                  {{
                    ownershipIsCompanyLead
                      ? 'Este número é um lead da empresa'
                      : 'Contato com outro vendedor'
                  }}
                </h3>
              </div>
              <p
                v-if="ownershipIsCompanyLead"
                class="text-sm text-n-slate-11 mb-4"
              >
                O contato
                <strong class="text-n-slate-12">+{{ phone }}</strong>
                já pertence à base da empresa, mesmo sem responsável definido no
                momento. Conversar com ele pelo seu número pessoal precisa da
                liberação do gestor.
              </p>
              <p v-else class="text-sm text-n-slate-11 mb-4">
                O contato
                <strong class="text-n-slate-12">+{{ phone }}</strong>
                {{ ownershipVerb }}
                <strong class="text-n-slate-12">{{
                  ownershipCheck.owner_name
                }}</strong
                >{{
                  ownershipCheck.inbox_name
                    ? ' na inbox "' + ownershipCheck.inbox_name + '"'
                    : ''
                }}.
              </p>
              <p
                v-if="ownershipCheck.personal_instance"
                class="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-300"
              >
                Se o gestor liberar, você atende pelo seu número — mas como
                atendimento da empresa, em "Meus": o lead continua da empresa, a
                carteira e o responsável não mudam, e o gestor segue enxergando
                a conversa. A tentativa e a liberação ficam registradas no
                histórico do contato.
              </p>
              <p class="text-xs text-n-slate-10 mb-4">
                Para iniciar conversa, peça autorização.
              </p>
              <div
                v-if="ownershipCheck.existing_request"
                class="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300 mb-4"
              >
                Você já tem uma solicitação pendente para este contato. Aguarde
                a resposta.
              </div>
              <div v-else>
                <label
                  class="text-xs text-n-slate-10 font-semibold uppercase block mb-1"
                >
                  Mensagem que pretende enviar (opcional)
                </label>
                <textarea
                  v-model="ownershipMessageIntent"
                  rows="3"
                  class="w-full bg-n-alpha-1 border border-n-strong rounded-lg px-3 py-2 text-sm text-n-slate-12 resize-none mb-3"
                  placeholder="Quero atender este lead porque…"
                />
              </div>
              <div class="flex gap-2 justify-end">
                <button
                  class="px-4 py-2 text-n-slate-11 text-sm hover:text-n-slate-12"
                  @click="dismissOwnershipCheck"
                >
                  Cancelar
                </button>
                <button
                  v-if="!ownershipCheck.existing_request"
                  :disabled="ownershipRequesting"
                  class="px-5 py-2 bg-n-brand text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                  @click="requestOwnership(ownershipMessageIntent)"
                >
                  {{
                    ownershipRequesting ? 'Enviando…' : 'Solicitar autorização'
                  }}
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Teleport>
  </span>
</template>

<style scoped>
.acl-quick-start-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}
/* O aviso de ownership abre por cima do seletor de canal, que continua montado
   para o usuário voltar e escolher outra instância ao cancelar. */
.acl-quick-start-overlay--stacked {
  z-index: 2147483501;
}
.acl-quick-start-modal {
  display: flex;
  flex-direction: column;
  width: min(560px, 92vw);
  height: min(78vh, 470px);
  max-height: min(78vh, 470px);
  padding: 16px;
  overflow: hidden;
  color: #111827;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
}
.acl-quick-start-modal__badge {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #7c3aed;
  background: #f3e8ff;
  border: 1px solid #e9d5ff;
  border-radius: 999px;
}
.acl-quick-start-modal__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.acl-quick-start-modal__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  color: #111827;
}
.acl-quick-start-modal__description {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: #6b7280;
}
.acl-quick-start-modal__description strong {
  color: #111827;
  font-weight: 700;
}
.acl-quick-start-modal__list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  padding-right: 6px;
  margin-top: 12px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c7cdd4 transparent;
}
.acl-quick-start-modal__list::-webkit-scrollbar {
  width: 7px;
}
.acl-quick-start-modal__list::-webkit-scrollbar-thumb {
  background: #c7cdd4;
  border-radius: 999px;
}
.acl-quick-start-modal__channel {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 52px;
  gap: 12px;
  padding: 9px 12px;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  transition:
    border-color 0.14s ease,
    box-shadow 0.14s ease,
    transform 0.14s ease;
}
.acl-quick-start-modal__channel:not(:disabled):hover {
  border-color: #c4b5fd;
  box-shadow: 0 4px 12px rgba(91, 33, 182, 0.13);
  transform: translateY(-1px);
}
.acl-quick-start-modal__channel--disabled {
  cursor: default;
}
.acl-quick-start-modal__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  font-size: 23px;
}
.acl-quick-start-modal__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}
.acl-quick-start-modal__name {
  overflow: hidden;
  color: #1f2937;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acl-quick-start-modal__subtitle {
  overflow: hidden;
  margin-top: 2px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acl-quick-start-modal__action {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  gap: 3px;
  color: #7c3aed;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
}
.acl-quick-start-modal__blocked {
  flex: 0 1 180px;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  text-align: right;
}
.acl-quick-start-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-top: 2px;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  color: #6b7280;
  background: #f9fafb;
  flex-shrink: 0;
  transition:
    color 0.14s ease,
    border-color 0.14s ease,
    background-color 0.14s ease;
}
.acl-quick-start-modal__close:hover:not(:disabled) {
  color: #111827;
  border-color: #9ca3af;
  background: #f3f4f6;
}
.acl-quick-start-modal__close .i-lucide-x {
  pointer-events: none;
}
:global(.dark) .acl-quick-start-modal {
  color: #f9fafb;
  background: #111315;
  border-color: #2f343a;
}
:global(.dark) .acl-quick-start-modal__title,
:global(.dark) .acl-quick-start-modal__description strong,
:global(.dark) .acl-quick-start-modal__name,
:global(.dark) .acl-quick-start-modal__close {
  color: #f9fafb;
}
:global(.dark) .acl-quick-start-modal__description,
:global(.dark) .acl-quick-start-modal__subtitle {
  color: #a1a1aa;
}
:global(.dark) .acl-quick-start-modal__channel {
  background: #171a1d;
  border-color: #2f343a;
}
:global(.dark) .acl-quick-start-modal__blocked {
  color: #cbd5e1;
}
:global(.dark) .acl-quick-start-modal__close {
  border-color: #3f454d;
  background: #1f2328;
}
:global(.dark) .acl-quick-start-modal__close:hover:not(:disabled) {
  border-color: #565d67;
  background: #252b31;
}
.acl-quick-start-modal__action,
.acl-quick-start-modal__blocked {
  flex-shrink: 0;
}
@media (max-width: 640px) {
  .acl-quick-start-modal {
    width: min(560px, 96vw);
    height: min(88vh, 470px);
    padding: 14px 12px 12px;
  }
  .acl-quick-start-modal__channel {
    display: grid;
    flex-shrink: 0;
    grid-template-columns: 26px minmax(0, 1fr) auto;
    align-items: center;
    gap: 3px 10px;
  }
  .acl-quick-start-modal__icon {
    grid-column: 1;
    grid-row: 1 / span 2;
  }
  .acl-quick-start-modal__content {
    grid-column: 2;
    grid-row: 1;
  }
  .acl-quick-start-modal__action {
    grid-column: 3;
    grid-row: 1;
    max-width: 92px;
    text-align: right;
  }
  .acl-quick-start-modal__action,
  .acl-quick-start-modal__blocked {
    flex-basis: auto;
    white-space: normal;
  }
  .acl-quick-start-modal__blocked {
    grid-column: 2 / -1;
    grid-row: 2;
    text-align: left;
  }
}
</style>
